import React from 'react'
import { useSetupWizard } from '../../contexts/SetupWizardContext'
import { useThemeColors } from '../../hooks/useThemeColors'
import { 
  BuildingOfficeIcon, 
  PaintBrushIcon, 
  BuildingStorefrontIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  CalendarDaysIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'

const steps = [
  { name: 'Organization', icon: BuildingOfficeIcon },
  { name: 'Branding', icon: PaintBrushIcon },
  { name: 'Facilities', icon: BuildingStorefrontIcon },
  { name: 'Services', icon: ClipboardDocumentListIcon },
  { name: 'Packages', icon: CurrencyDollarIcon },
  { name: 'Memberships', icon: CreditCardIcon },
  { name: 'Schedules', icon: CalendarDaysIcon },
  { name: 'Team', icon: UserGroupIcon },
]

export default function WizardProgress() {
  const { currentStep, validateCurrentStep, goToStep } = useSetupWizard()
  const colors = useThemeColors()

  // Adjust for welcome (0) and review (9) steps
  const activeStepIndex = currentStep - 1

  return (
    <div className="wizard-progress" style={{
      padding: '2rem',
      backgroundColor: colors.backgroundLight,
      borderBottom: `1px solid ${colors.border}`
    }}>
      <div className="progress-container" style={{
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
        <div className="progress-steps" style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative'
        }}>
          {/* Progress Line */}
          <div className="progress-line" style={{
            position: 'absolute',
            top: '20px',
            left: '40px',
            right: '40px',
            height: '2px',
            backgroundColor: colors.border,
            zIndex: 0
          }}>
            <div className="progress-line-active" style={{
              height: '100%',
              backgroundColor: colors.primary,
              width: `${(activeStepIndex / (steps.length - 1)) * 100}%`,
              transition: 'width 0.3s ease'
            }} />
          </div>

          {steps.map((step, index) => {
            const isActive = index === activeStepIndex
            const isCompleted = index < activeStepIndex
            const Icon = step.icon

            return (
              <div
                key={step.name}
                className="progress-step"
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  cursor: isCompleted ? 'pointer' : 'default',
                  zIndex: 1,
                  flex: 1
                }}
                onClick={() => isCompleted && goToStep(index + 1)}
              >
                <div
                  className="step-circle"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: isCompleted ? colors.primary : colors.background,
                    border: `2px solid ${isActive || isCompleted ? colors.primary : colors.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    transform: isActive ? 'scale(1.1)' : 'scale(1)'
                  }}
                >
                  {isCompleted ? (
                    <CheckIcon style={{ width: '20px', height: '20px', color: colors.background }} />
                  ) : (
                    <Icon style={{ 
                      width: '20px', 
                      height: '20px', 
                      color: isActive ? colors.primary : colors.textMuted 
                    }} />
                  )}
                </div>
                <span style={{
                  marginTop: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: isActive ? '600' : '400',
                  color: isActive ? colors.text : colors.textMuted,
                  textAlign: 'center'
                }}>
                  {step.name}
                </span>
              </div>
            )
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .progress-steps {
            gap: 0.5rem;
          }

          .progress-step span {
            font-size: 0.75rem !important;
          }

          .step-circle {
            width: 32px !important;
            height: 32px !important;
          }

          .step-circle svg {
            width: 16px !important;
            height: 16px !important;
          }

          .progress-line {
            left: 20px !important;
            right: 20px !important;
          }
        }
      `}</style>
    </div>
  )
}